<div class="p-4 h-full flex flex-col">
  <div class="flex items-center gap-4 mb-3 flex-wrap">
    <h1 class="text-xl font-semibold dark:text-white">NYSE Exhibit B Compliance Report</h1>
    <span class="text-xs text-gray-500 dark:text-gray-400">Live-compiled from F2-ADMIN.Exchange_Agreements. Redis-cached; invalidates on write.</span>
    <button type="button"
            class="w-6 h-6 rounded-full border border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-app-blue-100/40 text-sm font-semibold flex items-center justify-center"
            (click)="show_help = !show_help"
            title="How this report works">?</button>
  </div>

  <div *ngIf="show_help" class="mb-3 p-3 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-app-blue-100/40 text-xs text-gray-700 dark:text-gray-200 max-w-3xl">
    <div class="flex items-start justify-between mb-2">
      <strong class="text-sm">How this report works</strong>
      <button type="button" class="text-gray-500 hover:text-gray-800 dark:hover:text-white" (click)="show_help = false" title="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <p class="mb-2">One row per <em>(user, customer)</em> in NYSE Exhibit B submission format (36 fields per §4.2.4/§4.2.5), plus display-only columns for internal review (Customer, Scanners, Pro/Non-Pro, First Signed, Deactivated, F2 User ID).</p>
    <p class="mb-1"><strong>Data sources</strong> (merged, deduped on <code>(user_id, customer)</code>):</p>
    <ul class="list-disc pl-5 mb-2 space-y-0.5">
      <li><code>F2-ADMIN.Exchange_Agreements</code> (main <code>10.10.10.52</code>) — current unified agreement store, main wins on collision.</li>
      <li><code>F2-ADMIN.Exchange_Agreements</code> (legacy <code>66.85.152.90</code>) — historical snapshot, older signers.</li>
      <li><code>F2-ADMIN.UserTracking</code> + <code>Legacy_UserTracking</code> — per-scanner subscription history driving Scanners list, Deactivated date, and the &quot;active during window&quot; date-range filter.</li>
      <li><code>F2-ADMIN.Exchange_Aggrement_Graphem</code> (main) — legacy tracking-side snapshot (~1,581 docs), authoritative <em>First Signed</em> when present (overrides the local roll-up for pre-2025 signers).</li>
    </ul>
    <p class="mb-1"><strong>Filters</strong> (mirrored to URL — refresh + share preserve state):</p>
    <ul class="list-disc pl-5 mb-2 space-y-0.5">
      <li><em>Filter by Customer</em> — multi-select; empty = all customers.</li>
      <li><em>Active from / Active to</em> — keeps rows whose scanner subscription overlapped this window (not the same as &quot;signed during&quot;). Quick-picks: This month / Last month / 2mo ago.</li>
    </ul>
    <p class="mb-1"><strong>Exports</strong>:</p>
    <ul class="list-disc pl-5 mb-2 space-y-0.5">
      <li><em>Copy to Excel</em> — TSV to clipboard, all visible columns including display-only, for internal review.</li>
      <li><em>Export CSV (all, comma)</em> — comma-delimited, all visible grid columns, internal use.</li>
      <li><em>Non-Pro Exhibit B (36-col)</em> — NYSE §4.2.4 Nonprofessional Exhibit B pipe-delimited file. Full subscriber details + Q1-Q11 responses. Multi-row per user (one per usage period).</li>
      <li><em>Non-Pro Entitlement (7-col)</em> — Nonprofessional Entitlement Record pipe-delimited. <code>USER_ID | SUBSCRIBER_NAME | QUANTITY | ACTIVATION_DATE (YYYYMMDD) | DEACTIVATION_DATE | TYPE_OF_DATA | TYPE_OF_PRODUCT</code>. 2 rows per user for CTA Network A + B.</li>
      <li><em>Note</em>: both exhibit exports are for <strong>Nonprofessional users only</strong>. No Pro exhibit report exists currently.</li>
    </ul>
    <p><strong>Caching</strong>: Redis-cached per (customer, date range). Cache is invalidated on any agreement write (Pro flip, edit, new sign) and on schema/logic changes via a bumped cache-version key.</p>
  </div>

  <div class="flex items-center gap-3 mb-3 flex-wrap">
    <div class="w-[220px]">
      <mat-select class="filter-select" [formControl]="filter_customers" multiple placeholder="Filter by Customer">
        <mat-option *ngFor="let c of customers" [value]="c.id">{{ c.name }}</mat-option>
      </mat-select>
    </div>

    <!-- IT-F2-426 (Mike 2026-09-24 checklist 72f22d44): richer tooltip on
         the Date field label. Explains what each option actually filters
         by so operators don't have to guess (or reach for §9.5). -->
    <label class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
      <span [title]="date_field_help_tooltip()" class="underline decoration-dotted decoration-gray-400 cursor-help">Date field</span>
      <select [formControl]="filter_date_field"
              [title]="date_field_help_tooltip()"
              class="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-app-blue-100/40 px-2 py-1 text-sm">
        <option *ngFor="let o of date_field_options" [value]="o.id" [title]="date_field_option_tooltip(o.id)">{{ o.label }}</option>
      </select>
    </label>

    <label class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
      <span title="First month of the range (inclusive). Leave blank for open-ended start.">From month</span>
      <select [formControl]="filter_month_from" class="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-app-blue-100/40 px-2 py-1 text-sm min-w-[140px]">
        <option value="">(any)</option>
        <option *ngFor="let m of month_options" [value]="m.id">{{ m.label }}</option>
      </select>
    </label>

    <!-- IT-F2-426 (Mike 2026-09-24 checklist 3b879378): Lock To=From
         checkbox for one-month select. When ticked, To mirrors From and
         the dropdown is disabled. When unticked, To dropdown shows only
         months ≥ From to prevent inverting the range. -->
    <label class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
           title="Lock To month = From month for a fast single-month pick. Uncheck to pick a range (To dropdown limits itself to months ≥ From).">
      <input type="checkbox" [checked]="lock_to_month" (change)="on_lock_to_month_change($any($event.target).checked)"
             class="rounded border-gray-300 dark:border-gray-600 accent-blue-600" />
      <span>Lock to = from</span>
    </label>
    <label class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
      <span [title]="lock_to_month ? 'To month locked to From month — untick the lock checkbox to pick a different To.' : 'Last month of the range (inclusive). Leave blank for open-ended end. Options limited to months ≥ From to prevent inverting the range.'">To month</span>
      <select [formControl]="filter_month_to"
              class="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-app-blue-100/40 px-2 py-1 text-sm min-w-[140px] disabled:opacity-60 disabled:cursor-not-allowed">
        <option value="">(any)</option>
        <option *ngFor="let m of to_month_options" [value]="m.id">{{ m.label }}</option>
      </select>
    </label>

    <div class="flex items-center gap-1">
      <button type="button" class="dark-btn text-xs px-2 py-1" (click)="pick_month(0)" title="Active this month">This month</button>
      <button type="button" class="dark-btn text-xs px-2 py-1" (click)="pick_month(-1)" title="Active last month">Last month</button>
      <button type="button" class="dark-btn text-xs px-2 py-1" (click)="pick_month(-2)" title="Active 2 months ago">2mo ago</button>
    </div>

    <button type="button" class="dark-btn min-w-[105px]" (click)="refresh()">
      <i class="fa-solid fa-fw fa-sync mr-1 dark:text-green-600"></i> Refresh
    </button>

    <button type="button" class="dark-btn min-w-[125px]" (click)="clear_filters()">
      <i class="fa-solid fa-xmark mr-1 dark:text-green-600"></i> Clear Filters
    </button>

    <button *ngIf="selected_count > 0"
            type="button"
            class="dark-btn"
            (click)="clear_selection()"
            title="Deselect all rows so exports use the full filtered set again">
      <i class="fa-solid fa-square-minus mr-1 text-yellow-400"></i> Clear Selection ({{ selected_count }})
    </button>

    <button type="button"
            class="dark-btn"
            (click)="copy_debug()"
            title="Copy filter state + row counts + first 5 sample rows to clipboard as JSON for debugging">
      <i class="fa-solid fa-bug mr-1 text-purple-400"></i> Debug Copy
    </button>
    <!-- IT-F2-426 b6683cc5 (Mike 2026-09-25): opt-in to include the whole
         current view (visible rows for whatever tab is active) in the
         Debug Copy payload so the numbers on screen match the JSON. -->
    <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none"
           title="Include the current tab's visible rows in the Debug Copy so the numbers on screen match the JSON. Preference persists across reloads.">
      <input type="checkbox"
             [ngModel]="debug_copy_include_view_data"
             (ngModelChange)="_persist_debug_include($event)"
             class="cursor-pointer">
      <span>include view data</span>
    </label>

    <div class="grow"></div>

    <button type="button" class="dark-btn" (click)="copy_to_excel()">
      <i class="fa-solid fa-copy mr-1 dark:text-green-600"></i> Copy to Excel
    </button>

    <button type="button" class="dark-btn" (click)="export_all_visible_csv()" title="Comma-delimited, all visible grid columns — internal use">
      <i class="fa-solid fa-file-arrow-down mr-1 dark:text-green-600"></i> Export CSV (all, comma)
    </button>

    <button type="button" class="dark-btn" (click)="export_csv_nonpro_exhibit()"
            title="§4.2.4 Nonprofessional Exhibit B — 36-col detailed subscriber record, one row per (user, usage-period)">
      <i class="fa-solid fa-file-arrow-down mr-1 text-green-400"></i> Non-Pro Exhibit B (36-col)
    </button>

    <button type="button" class="dark-btn" (click)="export_csv_nonpro_entitlement()"
            title="Nonprofessional Entitlement Record — 7-col compact format, 2 rows per user for CTA Network A + B">
      <i class="fa-solid fa-file-arrow-down mr-1 text-green-400"></i> Non-Pro Entitlement (7-col)
    </button>

    <button type="button" class="dark-btn" (click)="export_csv_employee_exhibit()"
            title="§4.2.5 Employee Exhibit B — 36-col detailed subscriber record, F2 internal admin/professional users only (customer===f2 + PRO_STATUS===Pro) (IT-F2-367)">
      <i class="fa-solid fa-file-arrow-down mr-1 text-amber-400"></i> Employee Exhibit B (36-col)
    </button>

    <button type="button" class="dark-btn" (click)="export_csv_employee_entitlement()"
            title="§9.2 Professional Users Entitlement Record — 17-col format for F2 internal admin/professional users. Firm-level fields (VAN, legal name, installation address) come from Customer.f2 company info; pro CTA codes come from the CTA Product Codes table on Company Info. Click Edit Company Info to change">
      <i class="fa-solid fa-file-arrow-down mr-1 text-amber-400"></i> Employee Entitlement (17-col)
    </button>

    <button type="button" class="dark-btn" (click)="openCompanyInfoModal()"
            title="Edit firm compliance metadata (VAN, VAN_NAME, legal company name, installation address, default TYPE_OF_PRODUCT) stored on Customer.f2 — fed into the Employee entitlement report">
      <i class="fa-solid fa-pen-to-square mr-1 text-blue-400"></i> Edit Company Info
    </button>
  </div>

  <!-- IT-F2-367 — Edit Company Info modal. Firm compliance metadata for
       NYSE §9.2 Professional Users Entitlement Record generation. Persisted
       to Customer.f2 via POST /admin/company-info. -->
  <div *ngIf="show_company_info_modal"
       class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
       (click)="closeCompanyInfoModal()">
    <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
         (click)="$event.stopPropagation()">
      <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
        <h3 class="font-semibold text-gray-900 dark:text-white">Edit Company Info — F2 Tech LLC (Customer.f2)</h3>
        <button class="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white" (click)="closeCompanyInfoModal()">✕</button>
      </div>
      <div class="p-4 space-y-3 text-sm">
        <div class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
          Fed into the NYSE §9.2 Professional Users Entitlement Record. Persisted to <code class="text-gray-700 dark:text-gray-300">F2-ADMIN.Customers._id=f2</code>.
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Legal Company Name</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.legal_name" placeholder="e.g. F2 Tech LLC" />
          </div>
          <div>
            <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">VAN (NYSE Vendor Account Number)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.nyse_vendor_account_number" placeholder="e.g. QNX-P21351" />
          </div>
        </div>

        <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1">Firm registered address</div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Address</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_company_info.address" />
        </div>
        <div class="grid grid-cols-4 gap-3">
          <div class="col-span-2">
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">City</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.city" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">State</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.state" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">ZIP</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.zip_code" />
          </div>
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Country</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_company_info.country" placeholder="US" />
        </div>

        <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1 pt-2">Data-center location — VAN_NAME + installation fallback</div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Location Name (VAN_NAME)</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.data_center_location.name" placeholder="e.g. phoenixNAP" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Address</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.data_center_location.address" />
          </div>
        </div>
        <div class="grid grid-cols-4 gap-3">
          <div class="col-span-2">
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">City</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.data_center_location.city" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">State</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.data_center_location.state" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">ZIP</label>
            <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="editing_company_info.data_center_location.zip_code" />
          </div>
        </div>

        <div class="pt-2">
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Default PRODUCT (NYSE §9.2 spec enum)</label>
          <select class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                  [(ngModel)]="editing_company_info.default_type_of_product">
            <option *ngFor="let p of nyse_product_type_options" [value]="p">{{p}}</option>
          </select>
        </div>

        <!-- IT-F2-426 (Mike 2026-09-24 c/d1856280): Pro-tier realtime data
             reporting rule. Excludes non-employee Pros from the compliance
             report set when they're on 15-min delayed data. Optional cutoff
             date brings them back in from that date forward (rollout start).
             Employees (customer==='f2') always reported as Pro regardless. -->
        <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1 pt-3">
          <span>Pro-tier realtime data reporting rule</span>
        </div>
        <label class="flex items-start gap-2 text-sm py-1 cursor-pointer">
          <input type="checkbox" [(ngModel)]="editing_company_info.pro_15min_delayed"
                 class="mt-0.5 accent-blue-600" />
          <span class="flex-1 text-gray-800 dark:text-gray-200">
            <b>Pro users are 15-minute delayed</b> &mdash; exclude Pro users from the compliance reports (they&rsquo;re not receiving live data, so not reportable to NYSE).
            <br /><span class="text-[11px] text-gray-500 dark:text-gray-400">Employees (customer <code>f2</code>) always report as Pro regardless of this setting.</span>
          </span>
        </label>
        <div [class.opacity-50]="!editing_company_info.pro_15min_delayed">
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1 pt-1">Pro live-data start date <span class="normal-case text-[10px]">(optional cutoff — Pros only appear on reports from this date onwards)</span></label>
          <input type="date"
                 class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_company_info.pro_live_data_start_date"
                 [disabled]="!editing_company_info.pro_15min_delayed" />
          <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Blank while the checkbox is ticked = drop <em>all</em> non-employee Pros. Set a date once live data is granted to Pros — they'll start appearing on the report from that date forward.</p>
        </div>

        <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1 pt-2">
          <span>NYSE Market-Data Feeds — check the ones F2 subscribes to</span>
        </div>
        <p class="text-[11px] text-gray-500 dark:text-gray-400">
          Ticked feeds land on the Employee Entitlement (§9.2) export by human-readable name — no numeric CTA product code required.
          Grouped by tape (Network A / Network B / Options / Bonds) per NYSE Comprehensive Market Data Policy Package.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 py-1">
          <div *ngFor="let g of ctaCatalogGrouped">
            <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 pt-1 pb-0.5 font-semibold">{{g.network}}</div>
            <label *ngFor="let it of g.items" class="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-app-black-200 rounded px-1">
              <input type="checkbox" [checked]="isCtaCatalogItemChecked(it)" (change)="toggleCtaCatalogItem(it)" class="accent-blue-600" />
              <span>{{it.label}}</span>
            </label>
          </div>
        </div>

        <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1 pt-3 flex items-center justify-between">
          <span>Custom Entries — for numeric CTA codes or bespoke labels not in the catalog</span>
          <button class="dark-btn text-xs px-2 py-0.5" (click)="addCtaCode()"><i class="fa-solid fa-plus mr-1 text-green-500"></i>Add</button>
        </div>
        <p class="text-[11px] text-gray-500 dark:text-gray-400">
          Only needed if NYSE issued F2 a firm-specific numeric product code (from the Fee Schedules) or you want to override the display label.
          Rows added via the checkboxes above also appear here.
        </p>
        <table class="w-full text-xs" *ngIf="(editing_company_info.nyse_cta_codes || []).length > 0">
          <thead class="text-gray-500 dark:text-gray-400 uppercase text-[10px] tracking-wide">
            <tr>
              <th class="text-left pr-2 pb-1">Code</th>
              <th class="text-left pr-2 pb-1">Label</th>
              <th class="text-left pr-2 pb-1">Tier</th>
              <th class="text-left pr-2 pb-1">Product (spec enum)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let c of editing_company_info.nyse_cta_codes; let i = index">
              <td class="pr-2 pb-1"><input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1" [(ngModel)]="c.code" placeholder="e.g. 35009" /></td>
              <td class="pr-2 pb-1"><input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1" [(ngModel)]="c.label" list="nyseCtaTypeSuggestions" placeholder="e.g. CTA Network A" /></td>
              <td class="pr-2 pb-1">
                <select class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1" [(ngModel)]="c.tier">
                  <option value="pro">Pro</option>
                  <option value="nonpro">Non-Pro</option>
                </select>
              </td>
              <td class="pr-2 pb-1">
                <select class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1" [(ngModel)]="c.product">
                  <option *ngFor="let p of nyse_product_type_options" [value]="p">{{p}}</option>
                </select>
              </td>
              <td class="pb-1 text-right"><button class="text-red-500 hover:text-red-600 text-sm" (click)="removeCtaCode(i)" title="Remove">✕</button></td>
            </tr>
          </tbody>
        </table>
        <div *ngIf="(editing_company_info.nyse_cta_codes || []).length === 0" class="text-[11px] text-gray-500 dark:text-gray-400 italic">
          No codes yet. Click Add to enter your first CTA product code.
        </div>

        <!-- Datalist backing the Label field — NYSE preset data types from
             Comprehensive Market Data Policies PDF §7.3 + CTA network
             designators. Operator can pick a preset or type custom. -->
        <datalist id="nyseCtaTypeSuggestions">
          <option *ngFor="let s of nyse_data_type_suggestions" [value]="s"></option>
        </datalist>
      </div>
      <div class="px-4 py-3 border-t border-gray-200 dark:border-[#434863] flex justify-end gap-2">
        <button class="dark-btn" (click)="closeCompanyInfoModal()">Cancel</button>
        <button class="dark-btn bg-blue-600 text-white" (click)="saveCompanyInfo()">Save</button>
      </div>
    </div>
  </div>

  <div class="flex items-center gap-3 mb-3 flex-wrap text-xs">
    <span class="text-gray-500 dark:text-gray-400 font-semibold">View:</span>
    <div class="inline-flex rounded-md overflow-hidden border border-gray-400 dark:border-gray-500">
      <button type="button"
              class="px-3 py-1"
              [ngClass]="view_mode === 'users' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-app-blue-100/60'"
              (click)="set_view_mode('users')">Users</button>
      <button type="button"
              class="px-3 py-1 border-l border-gray-400 dark:border-gray-500"
              [ngClass]="view_mode === 'monthly_counts' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-app-blue-100/60'"
              (click)="set_view_mode('monthly_counts')">Counts by Month</button>
      <button type="button"
              class="px-3 py-1 border-l border-gray-400 dark:border-gray-500"
              [ngClass]="view_mode === 'login_periods' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-app-blue-100/60'"
              (click)="set_view_mode('login_periods')"
              title="One row per user per usage period (from Graphem_Compliance + AuthLogins main+legacy)">Login Periods</button>
      <button type="button"
              class="px-3 py-1 border-l border-gray-400 dark:border-gray-500"
              [ngClass]="view_mode === 'employees' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-app-blue-100/60'"
              (click)="set_view_mode('employees')"
              title="F2 employees — admin-managed Exchange_Agreement rows for internal professional users (IT-F2-367)">Employees</button>
      <!-- IT-F2-426 (Mike 2026-09-24 c/b89b32fe + c/49315c17): alternate
           overall view — unique users per DB per month across all 4
           login sources. -->
      <button type="button"
              class="px-3 py-1 border-l border-gray-400 dark:border-gray-500"
              [ngClass]="view_mode === 'source_coverage' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-app-blue-100/60'"
              (click)="set_view_mode('source_coverage')"
              title="Alternate overall view — unique users per DB per month across all 4 login sources (main+legacy AuthLogins + Graphem_Compliance + AuthLogins_Graphem). Pro/Non-Pro split via agreement pro_access join.">Source Coverage</button>
      <!-- IT-F2-426 checklist c0019445 (Mike 2026-09-24 c/6c21ee1e):
           dedicated Report tab showing event counts + unique users side
           by side. Cell click opens the drill-down modal below. -->
      <button type="button"
              class="px-3 py-1 border-l border-gray-400 dark:border-gray-500"
              [ngClass]="view_mode === 'report_page' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-app-blue-100/60'"
              (click)="set_view_mode('report_page')"
              title="Compliance report page — event-count table + unique-user table side by side. Click any cell to see how the number was calculated and export a per-user drill-down to Excel.">Report</button>
      <!-- IT-F2-391 c/a4c363b1 (2026-09-15) — Review tab moved to its
           own admin panel at /admin/exchange-agreement-review (Mike:
           "lets break the compliance review and editor out into its
           own separate panel instead of in the compliance report"). -->
    </div>
    <button *ngIf="view_mode === 'monthly_counts'"
            type="button"
            class="dark-btn text-xs px-2 py-1"
            (click)="copy_monthly_counts_to_excel()"
            title="Copy the current monthly counts to clipboard as TSV for Excel paste">
      <i class="fa-solid fa-copy mr-1 dark:text-green-600"></i> Copy Monthly to Excel
    </button>
    <button *ngIf="view_mode === 'monthly_counts'"
            type="button"
            class="dark-btn text-xs px-2 py-1"
            (click)="export_monthly_counts_csv()"
            title="Export the current monthly counts as CSV (respects ag-grid column filters)">
      <i class="fa-solid fa-file-arrow-down mr-1 dark:text-green-600"></i> Export Monthly Counts CSV
    </button>
    <span *ngIf="view_mode === 'monthly_counts'" class="text-gray-500 dark:text-gray-400">{{ monthly_counts_rows.length }} months</span>

    <!-- IT-F2-367 Employees view mode toolbar: Add Employee + row count -->
    <ng-container *ngIf="view_mode === 'employees'">
      <button type="button" class="dark-btn text-xs px-2 py-1" (click)="openNewEmployeeModal()"
              title="Create a new F2 employee agreement row (admin-side)">
        <i class="fa-solid fa-plus mr-1 text-green-500"></i> Add Employee
      </button>
      <span class="text-gray-500 dark:text-gray-400 text-xs">{{ employee_rows.length }} {{ employee_rows.length === 1 ? 'employee' : 'employees' }}</span>
    </ng-container>
  </div>

  <!-- IT-F2-367 Employees view mode: simple table (not ag-grid) -->
  <div *ngIf="view_mode === 'employees'" class="grow overflow-auto">
    <table class="w-full text-xs">
      <thead class="bg-gray-100 dark:bg-app-blue-100/40 text-gray-700 dark:text-gray-200 uppercase text-[10px] tracking-wide">
        <tr>
          <th class="text-left p-2">User ID</th>
          <th class="text-left p-2">Name</th>
          <th class="text-left p-2">Occupation / Title</th>
          <th class="text-left p-2">Address</th>
          <th class="text-left p-2">Activated</th>
          <th class="text-left p-2">Deactivated</th>
          <th class="text-center p-2">Pro</th>
          <th class="text-right p-2">Actions</th>
        </tr>
      </thead>
      <tbody class="text-gray-800 dark:text-gray-200">
        <tr *ngFor="let r of employee_rows" class="border-b border-gray-200 dark:border-[#434863] hover:bg-gray-50 dark:hover:bg-app-blue-100/20">
          <td class="p-2 font-mono">{{ r.user_id }}</td>
          <td class="p-2">{{ r.first_name }} {{ r.last_name }}</td>
          <td class="p-2">{{ r.occupation }}<span *ngIf="r.job_title"> / {{ r.job_title }}</span></td>
          <td class="p-2 text-[11px]">{{ r.address }}<span *ngIf="r.city">, {{ r.city }} {{ r.state }} {{ r.zip_code }}</span></td>
          <td class="p-2 text-[11px]">{{ (r.activation_date || r.created_at) ? ((r.activation_date || r.created_at) | date:'yyyy-MM-dd HH:mm z') : '—' }}</td>
          <td class="p-2 text-[11px]"><span *ngIf="r.deactivation_date">{{ r.deactivation_date | date:'yyyy-MM-dd HH:mm z' }}</span><span *ngIf="!r.deactivation_date" class="text-gray-500">active</span></td>
          <td class="p-2 text-center">
            <span *ngIf="r.pro_access" class="text-green-600 dark:text-green-400">✓ Pro</span>
            <span *ngIf="!r.pro_access" class="text-gray-500">—</span>
          </td>
          <td class="p-2 text-right">
            <button class="dark-btn text-xs px-2 py-1 mr-1" (click)="openEditEmployeeModal(r)">Edit</button>
            <button class="dark-btn text-xs px-2 py-1 text-red-500" (click)="deleteEmployee(r)">Delete</button>
          </td>
        </tr>
        <tr *ngIf="employee_rows.length === 0">
          <td colspan="8" class="p-6 text-center text-gray-500 dark:text-gray-400">
            No F2 employees yet. Click "Add Employee" to create the first agreement row.
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- IT-F2-426 (Mike 2026-09-24 checklist 739b08a0): plain-English
       explainer above the grid so a first-time viewer knows what they're
       looking at without opening the ? help panel. Sentence tracks the
       active view_mode + filter picks. -->
  <div *ngIf="view_mode !== 'employees' && view_mode !== 'report_page'"
       class="mb-2 px-3 py-2 rounded-md border border-blue-500/40 bg-blue-500/10 text-xs text-blue-800 dark:text-blue-200 flex items-start gap-2">
    <i class="fa-solid fa-circle-info mt-0.5"></i>
    <span>{{ grid_summary_sentence() }}</span>
  </div>
  <div *ngIf="view_mode !== 'employees' && view_mode !== 'report_page'" class="grow">
    <ag-grid-angular
      class="h-full w-full"
      [theme]="theme"
      [rowData]="view_mode === 'monthly_counts' ? monthly_counts_rows : (view_mode === 'source_coverage' ? source_coverage_rows : rows)"
      [columnDefs]="grid.columnDefs"
      [gridOptions]="grid.options"
      [statusBar]="grid.statusBar"
      (gridReady)="on_grid_ready($event)">
    </ag-grid-angular>
  </div>

  <!-- IT-F2-426 checklist c0019445 (Mike 2026-09-24 c/6c21ee1e): Report
       page — event counts + unique users side by side, click a cell to
       drill down into contributing users. Uses plain HTML tables (not
       ag-grid) so click handlers wire directly per (source, month, bucket)
       cell and both tables can be viewed at once on wide screens. -->
  <div *ngIf="view_mode === 'report_page'" class="grow overflow-auto">
    <div class="mb-2 px-3 py-2 rounded-md border border-blue-500/40 bg-blue-500/10 text-xs text-blue-800 dark:text-blue-200 flex items-start gap-2">
      <i class="fa-solid fa-circle-info mt-0.5"></i>
      <span>
        Two-panel compliance report. Left panel = raw event counts per (source, month) &mdash; how much traffic each database recorded.
        Right panel = distinct users per (source, month) &mdash; how many unique people that traffic came from.
        Both panels honor the customers filter <b>and</b> the Date From/To range from the top bar &mdash; narrow the window to scope both panels to a specific month or year.
        <b>Click any number</b> to open a drill-down popup listing the contributing users with a Copy-to-Excel button.
        Pro / Non-Pro / Pro-Delayed split via Exchange_Agreements.pro_access + the Pro 15-min-delayed settings from Company Info.
        Months are ET-anchored (America/New_York) for AuthLogins; Graphem sources are month-precise.
      </span>
    </div>
    <div *ngIf="report_page_loading" class="text-xs text-gray-500 dark:text-gray-400 mb-2">
      <i class="fa-solid fa-circle-notch fa-spin mr-1"></i> Compiling event counts + unique users&hellip;
    </div>
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <!-- LEFT: Event Counts -->
      <div class="border border-gray-300 dark:border-[#434863] rounded overflow-hidden">
        <div class="bg-gray-100 dark:bg-app-blue-100/40 px-3 py-2 flex items-center justify-between gap-2">
          <div class="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
            <i class="fa-solid fa-chart-column mr-1"></i> Event Counts
          </div>
          <div class="flex items-center gap-2">
            <button type="button" class="dark-btn text-xs px-2 py-0.5"
                    (click)="copy_source_monthly_to_excel('events')"
                    [disabled]="report_page_loading || source_events_rows.length === 0"
                    title="Copy event counts table to clipboard as TSV — paste directly into Excel / Google Sheets">
              <i class="fa-solid fa-copy mr-1 dark:text-green-600"></i> Copy to Excel
            </button>
            <div class="text-[10px] text-gray-500 dark:text-gray-400">{{ source_events_rows.length }} rows &middot; raw event totals</div>
          </div>
        </div>
        <div class="max-h-[70vh] overflow-auto">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 dark:bg-app-blue-100/20 text-gray-700 dark:text-gray-200 uppercase text-[10px] tracking-wide sticky top-0">
              <tr>
                <th class="text-left p-2">Source</th>
                <th class="text-left p-2">Month</th>
                <th class="text-right p-2">Pro</th>
                <th class="text-right p-2">Non-Pro</th>
                <th class="text-right p-2" title="Pros on 15-min delayed data tier">Pro-Delayed</th>
                <th class="text-right p-2" title="Users with NO Exchange_Agreement on file">Delayed</th>
                <th class="text-right p-2">Total</th>
              </tr>
            </thead>
            <tbody class="text-gray-800 dark:text-gray-200">
              <tr *ngFor="let r of source_events_rows"
                  class="border-b border-gray-200 dark:border-[#434863] hover:bg-gray-50 dark:hover:bg-app-blue-100/20">
                <td class="p-2">{{ r.SOURCE }}</td>
                <td class="p-2 font-mono">{{ r.MONTH }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'events', 'PRO')"
                    title="Click to see the Pro users contributing to this cell">{{ r.PRO | number }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'events', 'NON_PRO')"
                    title="Click to see the Non-Pro users contributing to this cell">{{ r.NON_PRO | number }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline"
                    [ngStyle]="{ 'color': (r.PRO_DELAYED || 0) > 0 ? '#f59e0b' : undefined }"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'events', 'PRO_DELAYED')"
                    title="Click to see the Pro-Delayed users contributing to this cell">{{ (r.PRO_DELAYED || 0) | number }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline italic"
                    [ngStyle]="{ 'color': (r.DELAYED || 0) > 0 ? '#9ca3af' : undefined }"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'events', 'DELAYED')"
                    title="Click to see the Delayed users (no Exchange_Agreement) contributing to this cell">{{ (r.DELAYED || 0) | number }}</td>
                <td class="p-2 text-right font-semibold cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'events', 'TOTAL')"
                    title="Click to see every user contributing to this cell">{{ r.TOTAL | number }}</td>
              </tr>
              <tr *ngIf="!report_page_loading && source_events_rows.length === 0">
                <td colspan="7" class="p-6 text-center text-gray-500 dark:text-gray-400">
                  No event data. Adjust the customer filter above or check that the source collections have entries.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- RIGHT: Unique Users -->
      <div class="border border-gray-300 dark:border-[#434863] rounded overflow-hidden">
        <div class="bg-gray-100 dark:bg-app-blue-100/40 px-3 py-2 flex items-center justify-between gap-2">
          <div class="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
            <i class="fa-solid fa-users mr-1"></i> Unique Users
          </div>
          <div class="flex items-center gap-2">
            <button type="button" class="dark-btn text-xs px-2 py-0.5"
                    (click)="copy_source_monthly_to_excel('uniques')"
                    [disabled]="report_page_loading || source_uniques_rows.length === 0"
                    title="Copy unique-user counts table to clipboard as TSV — paste directly into Excel / Google Sheets">
              <i class="fa-solid fa-copy mr-1 dark:text-green-600"></i> Copy to Excel
            </button>
            <div class="text-[10px] text-gray-500 dark:text-gray-400">{{ source_uniques_rows.length }} rows &middot; deduped per (email, customer)</div>
          </div>
        </div>
        <div class="max-h-[70vh] overflow-auto">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 dark:bg-app-blue-100/20 text-gray-700 dark:text-gray-200 uppercase text-[10px] tracking-wide sticky top-0">
              <tr>
                <th class="text-left p-2">Source</th>
                <th class="text-left p-2">Month</th>
                <th class="text-right p-2">Pro</th>
                <th class="text-right p-2">Non-Pro</th>
                <th class="text-right p-2" title="Pros on 15-min delayed data tier">Pro-Delayed</th>
                <th class="text-right p-2" title="Users with NO Exchange_Agreement on file">Delayed</th>
                <th class="text-right p-2">Total</th>
              </tr>
            </thead>
            <tbody class="text-gray-800 dark:text-gray-200">
              <tr *ngFor="let r of source_uniques_rows"
                  class="border-b border-gray-200 dark:border-[#434863] hover:bg-gray-50 dark:hover:bg-app-blue-100/20">
                <td class="p-2">{{ r.SOURCE }}</td>
                <td class="p-2 font-mono">{{ r.MONTH }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'uniques', 'PRO')"
                    title="Click to see the Pro users in this cell">{{ r.PRO | number }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'uniques', 'NON_PRO')"
                    title="Click to see the Non-Pro users in this cell">{{ r.NON_PRO | number }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline"
                    [ngStyle]="{ 'color': (r.PRO_DELAYED || 0) > 0 ? '#f59e0b' : undefined }"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'uniques', 'PRO_DELAYED')"
                    title="Click to see the Pro-Delayed users in this cell">{{ (r.PRO_DELAYED || 0) | number }}</td>
                <td class="p-2 text-right cursor-pointer hover:underline italic"
                    [ngStyle]="{ 'color': (r.DELAYED || 0) > 0 ? '#9ca3af' : undefined }"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'uniques', 'DELAYED')"
                    title="Click to see the Delayed users (no Exchange_Agreement) in this cell">{{ (r.DELAYED || 0) | number }}</td>
                <td class="p-2 text-right font-semibold cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
                    (click)="open_drill_modal(r.SOURCE, r.MONTH, 'uniques', 'TOTAL')"
                    title="Click to see every user in this cell">{{ r.TOTAL | number }}</td>
              </tr>
              <tr *ngIf="!report_page_loading && source_uniques_rows.length === 0">
                <td colspan="7" class="p-6 text-center text-gray-500 dark:text-gray-400">
                  No user data. Adjust the customer filter above or check that the source collections have entries.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- IT-F2-426 checklist c0019445 (Mike c/6c21ee1e): drill-down modal.
     Shows per-user rows contributing to the clicked cell + a Copy to
     Excel button + a calculation explainer so operators learn the
     dedup / bucketing rule for that source. -->
<div *ngIf="drill_open"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="close_drill_modal()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-5xl w-full max-h-[90vh] flex flex-col border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
      <div class="min-w-0">
        <div class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{{ drill_title }}</div>
        <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{{ drill_calc_explanation() }}</div>
      </div>
      <button class="dark-btn text-xs px-2 py-1 shrink-0" (click)="close_drill_modal()">
        <i class="fa-solid fa-xmark"></i> Close
      </button>
    </div>
    <div class="px-4 py-2 border-b border-gray-200 dark:border-[#434863] flex items-center gap-3 flex-wrap text-xs bg-gray-50 dark:bg-app-blue-100/10">
      <span class="text-gray-600 dark:text-gray-300"><b>{{ drill_totals.UNIQUES | number }}</b> unique</span>
      <span class="text-gray-600 dark:text-gray-300"><b>{{ drill_totals.EVENTS | number }}</b> events</span>
      <span class="text-gray-600 dark:text-gray-300">Pro: <b>{{ drill_totals.PRO | number }}</b></span>
      <span class="text-gray-600 dark:text-gray-300">Non-Pro: <b>{{ drill_totals.NON_PRO | number }}</b></span>
      <span class="text-gray-600 dark:text-gray-300" [ngStyle]="{ 'color': drill_totals.PRO_DELAYED > 0 ? '#f59e0b' : undefined }">Pro-Delayed: <b>{{ drill_totals.PRO_DELAYED | number }}</b></span>
      <span class="text-gray-600 dark:text-gray-300" [ngStyle]="{ 'color': drill_totals.DELAYED > 0 ? '#9ca3af' : undefined, 'font-style': 'italic' }">Delayed: <b>{{ drill_totals.DELAYED | number }}</b></span>
      <span class="text-gray-500 dark:text-gray-400 ml-auto">Filter:</span>
      <div class="inline-flex rounded-md overflow-hidden border border-gray-400 dark:border-gray-500">
        <button type="button" class="px-2 py-0.5"
                [ngClass]="drill_bucket === 'TOTAL' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200'"
                (click)="drill_bucket = 'TOTAL'; _apply_drill_bucket_filter()">All</button>
        <button type="button" class="px-2 py-0.5 border-l border-gray-400 dark:border-gray-500"
                [ngClass]="drill_bucket === 'PRO' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200'"
                (click)="drill_bucket = 'PRO'; _apply_drill_bucket_filter()">Pro</button>
        <button type="button" class="px-2 py-0.5 border-l border-gray-400 dark:border-gray-500"
                [ngClass]="drill_bucket === 'NON_PRO' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200'"
                (click)="drill_bucket = 'NON_PRO'; _apply_drill_bucket_filter()">Non-Pro</button>
        <button type="button" class="px-2 py-0.5 border-l border-gray-400 dark:border-gray-500"
                [ngClass]="drill_bucket === 'PRO_DELAYED' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200'"
                (click)="drill_bucket = 'PRO_DELAYED'; _apply_drill_bucket_filter()">Pro-Delayed</button>
        <button type="button" class="px-2 py-0.5 border-l border-gray-400 dark:border-gray-500"
                [ngClass]="drill_bucket === 'DELAYED' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 dark:bg-app-blue-100/40 dark:text-gray-200'"
                (click)="drill_bucket = 'DELAYED'; _apply_drill_bucket_filter()"
                title="Users with no Exchange_Agreement">Delayed</button>
      </div>
      <button type="button" class="dark-btn text-xs px-2 py-1 ml-2"
              (click)="copy_drill_debug()"
              title="Copy drill context + totals + first 5 rows as JSON for diagnosis">
        <i class="fa-solid fa-bug mr-1 text-purple-400"></i> Copy Debug
      </button>
      <button type="button" class="dark-btn text-xs px-2 py-1" (click)="copy_drill_to_excel()"
              [disabled]="drill_loading || drill_filtered_rows().length === 0"
              title="Copy the visible rows to clipboard as TSV — paste directly into Excel / Google Sheets">
        <i class="fa-solid fa-copy mr-1 dark:text-green-600"></i> Copy to Excel
      </button>
    </div>
    <!-- IT-F2-426 08a4a5da (Mike 2026-09-28): explicit min-height so ag-grid
         has room to measure — grow inside flex-col was collapsing to 0 in
         some layouts and the grid rendered empty. -->
    <div class="grow overflow-hidden" style="min-height: 400px; height: 60vh;">
      <div *ngIf="drill_loading" class="p-6 text-center text-xs text-gray-500 dark:text-gray-400">
        <i class="fa-solid fa-circle-notch fa-spin mr-1"></i> Loading contributing users&hellip;
      </div>
      <!-- IT-F2-426 18d4b626 (Mike 2026-09-28 c/b65cd473): full ag-grid so
           operators can filter / sort / resize while troubleshooting. -->
      <ag-grid-angular *ngIf="!drill_loading"
        class="h-full w-full"
        [theme]="theme"
        [rowData]="drill_rows"
        [columnDefs]="drill_grid_column_defs"
        [gridOptions]="drill_grid_options"
        (gridReady)="on_drill_grid_ready($event)">
      </ag-grid-angular>
    </div>
  </div>
</div>

<!-- IT-F2-426 18d4b626 (Mike 2026-09-28 c/b65cd473): nested "View Logins"
     modal showing this user's AuthLogins history (main + legacy) so
     operators can troubleshoot from a single (email, customer) row. -->
<div *ngIf="logins_open"
     class="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
     (click)="close_logins_modal()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-6xl w-full max-h-[90vh] flex flex-col border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
      <div class="min-w-0">
        <div class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
          <i class="fa-solid fa-clock-rotate-left mr-1 dark:text-sky-400"></i> Login history &mdash; {{ logins_email }}<span *ngIf="logins_customer"> &middot; {{ logins_customer }}</span><span *ngIf="logins_month" class="ml-2 px-1.5 py-0.5 text-[10px] bg-sky-500/20 text-sky-300 rounded">{{ logins_month }}</span>
        </div>
        <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          <span *ngIf="logins_month">Scoped to <b>{{ logins_month }}</b> (ET). </span>Login history combined across AuthLogins main + legacy + Graphem_Compliance + AuthLogins_Graphem, sorted by DATE DESC, ET-anchored, capped at 500 rows. Graphem sources are month-precision (Provider = <code>graphem-snapshot</code>).
        </div>
      </div>
      <button class="dark-btn text-xs px-2 py-1 shrink-0" (click)="close_logins_modal()">
        <i class="fa-solid fa-xmark"></i> Close
      </button>
    </div>
    <div class="px-4 py-2 border-b border-gray-200 dark:border-[#434863] flex items-center gap-3 flex-wrap text-xs bg-gray-50 dark:bg-app-blue-100/10">
      <span class="text-gray-600 dark:text-gray-300"><b>{{ logins_totals.rows | number }}</b> rows</span>
      <span class="text-gray-600 dark:text-gray-300">main: <b>{{ logins_totals.main | number }}</b></span>
      <span class="text-gray-600 dark:text-gray-300">legacy: <b>{{ logins_totals.legacy | number }}</b></span>
      <span *ngIf="logins_totals.graphem !== undefined" class="text-gray-600 dark:text-gray-300">graphem: <b>{{ logins_totals.graphem | number }}</b></span>
      <span *ngIf="logins_totals.al_graphem !== undefined" class="text-gray-600 dark:text-gray-300">al_graphem: <b>{{ logins_totals.al_graphem | number }}</b></span>
      <!-- IT-F2-426 ea7782ad (Mike 2026-09-28): Copy Debug so operators
           can share the exact query + totals when a lookup returns 0. -->
      <button type="button" class="dark-btn text-xs px-2 py-1 ml-auto"
              (click)="copy_logins_debug()"
              title="Copy query params + totals + first 5 rows as JSON for diagnosis">
        <i class="fa-solid fa-bug mr-1 text-purple-400"></i> Copy Debug
      </button>
      <button type="button" class="dark-btn text-xs px-2 py-1"
              [disabled]="logins_loading || logins_rows.length === 0"
              (click)="copy_logins_to_excel()">
        <i class="fa-solid fa-copy mr-1 dark:text-green-600"></i> Copy to Excel
      </button>
    </div>
    <!-- IT-F2-426 549c2479 (Mike 2026-09-28): call out when the window
         has NO per-event AuthLogins data and only Graphem month snapshots
         hit — otherwise the operator wonders why they only see the 1st
         of the month, once per source. -->
    <div *ngIf="!logins_loading && logins_totals.main === 0 && logins_totals.legacy === 0 && ((logins_totals.graphem || 0) + (logins_totals.al_graphem || 0)) > 0"
         class="px-4 py-2 border-b border-yellow-500/30 bg-yellow-500/10 text-xs text-yellow-800 dark:text-yellow-200 flex items-start gap-2">
      <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
      <span>
        No per-event <b>AuthLogins</b> entries hit this window &mdash; only <b>Graphem</b> month-precision snapshots.
        Graphem is imported historical data (Provider = <code>graphem-snapshot</code>) flagging monthly-active users,
        not individual login events. Each row = one (user, customer, month) match from that source.
      </span>
    </div>
    <div class="grow overflow-hidden" style="min-height: 400px; height: 60vh;">
      <div *ngIf="logins_loading" class="p-6 text-center text-xs text-gray-500 dark:text-gray-400">
        <i class="fa-solid fa-circle-notch fa-spin mr-1"></i> Loading login history&hellip;
      </div>
      <ag-grid-angular *ngIf="!logins_loading"
        class="h-full w-full"
        [theme]="theme"
        [rowData]="logins_rows"
        [columnDefs]="logins_grid_column_defs"
        [gridOptions]="logins_grid_options"
        (gridReady)="on_logins_grid_ready($event)">
      </ag-grid-angular>
    </div>
  </div>
</div>

<!-- IT-F2-367 — Add / Edit Employee modal. Upserts an Exchange_Agreements
     row for an F2 employee via POST /admin/employee. Fields cover NYSE
     §9.2 output needs (name/contact/address/employer). -->
<div *ngIf="show_employee_modal"
     class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
     (click)="closeEmployeeModal()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="px-4 py-3 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
      <h3 class="font-semibold text-gray-900 dark:text-white">
        <ng-container *ngIf="is_new_employee">Add F2 Employee</ng-container>
        <ng-container *ngIf="!is_new_employee">Edit F2 Employee — {{ editing_employee.user_id }}</ng-container>
      </h3>
      <button class="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white" (click)="closeEmployeeModal()">✕</button>
    </div>
    <div class="p-4 space-y-3 text-sm">
      <div class="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
        Persists to <code class="text-gray-700 dark:text-gray-300">F2-ADMIN.Exchange_Agreements</code> on <code>(user_id.lower(), customer='f2')</code> key. Fields feed NYSE §9.2 Employee entitlement report.
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">User ID (email, primary key)</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 disabled:bg-gray-100 disabled:dark:bg-app-black-500"
                 [(ngModel)]="editing_employee.user_id"
                 [disabled]="!is_new_employee"
                 placeholder="e.g. m.fragomeni@f2-tech.com" />
        </div>
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Email (display)</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.email" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">First name</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.first_name" />
        </div>
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Last name</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.last_name" />
        </div>
      </div>

      <div class="grid grid-cols-3 gap-3">
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Phone</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.phone" />
        </div>
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Occupation</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.occupation" placeholder="e.g. Developer Founder" />
        </div>
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Title / Position</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.job_title" />
        </div>
      </div>

      <div>
        <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Employment function</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="editing_employee.employment_function" placeholder="e.g. Software Engineering, Firm principal" />
      </div>

      <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1 pt-2">Subscriber address (INSTALLATION ADDRESS in NYSE §9.2)</div>
      <div>
        <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Address</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="editing_employee.address" />
      </div>
      <div class="grid grid-cols-4 gap-3">
        <div class="col-span-2">
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">City</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.city" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">State</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.state" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">ZIP</label>
          <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.zip_code" />
        </div>
      </div>
      <div>
        <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Country</label>
        <input class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
               [(ngModel)]="editing_employee.country" placeholder="US" />
      </div>

      <div class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863] pb-1 pt-2">Report dates — used by NYSE §9.2 Employee Entitlement export</div>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Activation date (ACTIVATION_DATE)</label>
          <input type="date"
                 class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.activation_date" />
          <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Blank = falls back to <code>created_at</code> or scanner tracking.</p>
        </div>
        <div>
          <label class="block text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Deactivation date (DEACTIVATION_DATE)</label>
          <input type="date"
                 class="w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                 [(ngModel)]="editing_employee.deactivation_date" />
          <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Blank = still active (NYSE spec allows blank).</p>
        </div>
      </div>

      <div class="flex items-center gap-2 pt-2">
        <input type="checkbox" [(ngModel)]="editing_employee.pro_access" id="empPro" />
        <label for="empPro" class="text-sm text-gray-700 dark:text-gray-200">Pro access (Professional user — required for Employee entitlement report inclusion)</label>
      </div>

      <div class="text-[11px] text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-[#434863]">
        Employer block (auto-filled from Company Info): <b>{{ editing_employee.employer?.name || '—' }}</b>, {{ editing_employee.employer?.address }} {{ editing_employee.employer?.city }} {{ editing_employee.employer?.state }} {{ editing_employee.employer?.zip_code }}
      </div>
    </div>
    <div class="px-4 py-3 border-t border-gray-200 dark:border-[#434863] flex justify-end gap-2">
      <button class="dark-btn" (click)="closeEmployeeModal()">Cancel</button>
      <button class="dark-btn bg-blue-600 text-white" (click)="saveEmployee()">Save</button>
    </div>
  </div>
</div>

<!-- IT-F2-391 c/a4c363b1 (2026-09-15) — "View Application" modal moved
     along with the Review workflow to ExchangeAgreementReviewComponent
     at /admin/exchange-agreement-review. -->
